<script setup lang="ts">
import type { NodeViewProps } from '@tiptap/core';
import { NodeViewWrapper } from '@tiptap/vue-3';
import { BookMarked } from 'lucide-vue-next';
import { computed, inject } from 'vue';
import type { Ref } from 'vue';
import BibliographyView from '@/components/BibliographyView.vue';
import type { ReferenceItem } from '@/components/references/ReferenceListItem.vue';
import type { CitationStyle } from '@/lib/citations/formatters';

/**
 * The bibliography node view receives references, citedIds, citedOrder, and style
 * via Vue's provide/inject from the EditorCanvas parent.
 */
defineProps<NodeViewProps>();

const injectedRefs = inject<Ref<ReferenceItem[]>>('editorReferences');
const _citedIdsRef = inject<Ref<ReadonlySet<string>>>('editorCitedIds');
const _citedOrderRef = inject<Ref<string[]>>('editorCitedOrder');
const citationStyle = inject<string>('editorCitationStyle', 'apa');

const references = computed<ReferenceItem[]>(() => injectedRefs?.value ?? []);
const citedIds = computed<ReadonlySet<string>>(
    () => _citedIdsRef?.value ?? new Set(),
);
const citedOrder = computed<string[]>(() => _citedOrderRef?.value ?? []);
const activeStyle = computed<CitationStyle>(
    () => (citationStyle as CitationStyle) ?? 'apa',
);
</script>

<template>
    <NodeViewWrapper
        class="bibliography-block not-prose my-6 rounded-xl border border-border/40 bg-muted/30 p-5"
    >
        <div class="mb-3 flex items-center gap-2">
            <BookMarked class="h-4 w-4 text-muted-foreground" />
            <p class="text-sm font-semibold">Bibliographie</p>
        </div>

        <BibliographyView
            :references="references"
            :cited-ids="citedIds"
            :cited-order="citedOrder"
            :style="activeStyle"
        />
    </NodeViewWrapper>
</template>
